import { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import {
  Container, Stack, Title, Text, Paper, Group, Button, Loader, Center, Badge,
} from '@mantine/core'
import { IconArrowLeft, IconLock, IconLockOpen, IconPlayerPlay } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { facilitatorService } from '../services/facilitatorService'
import { facilitatedStoriesAtom } from '../atoms/authAtoms'

/**
 * Facilitator session-release dashboard. A facilitator (e.g. a course
 * instructor) opens this in the room and presses one button to advance their
 * cohort to the next session. Releasing is CUMULATIVE: "release through session
 * N" activates sessions 1..N and locks the rest, so an over-advance is undone
 * by releasing a lower N.
 *
 * Gated three ways: ProtectedRoute (authenticated) wraps the route; this page
 * additionally checks the story is in the user's facilitated list (UX); and the
 * backend authorizes :release_session (the real enforcement). A non-facilitator
 * who somehow reaches the URL gets bounced home and the API would 403 anyway.
 */
export default function FacilitatorDashboard() {
  const { storyId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const facilitatedStories = useAtomValue(facilitatedStoriesAtom)

  const facilitatedStory = facilitatedStories.find((s) => s.id === storyId)
  const isFacilitatorForThis = !!facilitatedStory

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['facilitatorSessions', storyId],
    queryFn: () => facilitatorService.getSessions(storyId),
    enabled: !!storyId && isFacilitatorForThis,
    refetchInterval: 15000, // keep the room's view current if released elsewhere
  })

  const ordered = useMemo(
    () => [...sessions].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [sessions],
  )

  // Released-through = length of the leading run of active sessions.
  const releasedThrough = useMemo(() => {
    let n = 0
    for (const s of ordered) {
      if (s.active) n += 1
      else break
    }
    return n
  }, [ordered])

  const releaseMutation = useMutation({
    mutationFn: (through) => facilitatorService.releaseSession(storyId, through),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['facilitatorSessions', storyId] })
      notifications.show({
        title: 'Released',
        message: `Cohort is now through session ${data.released_through} of ${data.total_sessions}.`,
        color: 'green',
      })
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to release session',
        color: 'red',
      })
    },
  })

  if (!isFacilitatorForThis) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Text c="red.4" fw={600} ff="Orbitron, sans-serif">Not a facilitator</Text>
          <Text c="dimmed" size="sm">You don't facilitate this story.</Text>
          <Button leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/')}>
            Home
          </Button>
        </Stack>
      </Container>
    )
  }

  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <Center py="xl"><Loader color="cyan" /></Center>
      </Container>
    )
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2} ff="Orbitron, sans-serif" c="cyan.2">
              Thread gates: {facilitatedStory.name}
            </Title>
            <Text c="dimmed" size="sm">
              Released through <strong>session {releasedThrough}</strong> of {ordered.length}.
              Releasing is cumulative — choosing a session opens everything up to it; an
              earlier one steps back.
            </Text>
          </div>
          <Button variant="light" color="grape" leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(
              facilitatedStory?.group_id
                ? `/facilitate/group/${facilitatedStory.group_id}`
                : `/facilitate/${storyId}/teams`, // fallback: legacy redirect resolves a group
            )}>
            Back to team management
          </Button>
        </Group>

        <Stack gap="xs">
          {ordered.map((session, idx) => {
            const sessionNumber = idx + 1
            const isReleased = sessionNumber <= releasedThrough
            const isNext = sessionNumber === releasedThrough + 1
            return (
              <Paper
                key={session.id}
                withBorder
                p="md"
                radius="md"
                style={{ background: 'var(--bw-panel-bg)' }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="md" wrap="nowrap">
                    <Text fw={700} c="dimmed" w={100} ff="Orbitron, sans-serif">
                      Session {sessionNumber}
                    </Text>
                    <Text truncate>{session.name || `Session ${sessionNumber}`}</Text>
                  </Group>
                  <Group gap="sm" wrap="nowrap">
                    <Badge color={isReleased ? 'green' : 'gray'} variant={isReleased ? 'filled' : 'light'}>
                      {isReleased ? 'Released' : 'Locked'}
                    </Badge>
                    {isReleased ? (
                      <Button
                        size="sm"
                        variant="subtle"
                        color="yellow"
                        leftSection={<IconLock size={16} />}
                        loading={releaseMutation.isPending}
                        onClick={() => releaseMutation.mutate(sessionNumber - 1)}
                      >
                        Lock from here
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant={isNext ? 'filled' : 'light'}
                        color="cyan"
                        leftSection={isNext ? <IconPlayerPlay size={16} /> : <IconLockOpen size={16} />}
                        loading={releaseMutation.isPending}
                        onClick={() => releaseMutation.mutate(sessionNumber)}
                      >
                        {isNext ? 'Release next session' : 'Release through here'}
                      </Button>
                    )}
                  </Group>
                </Group>
              </Paper>
            )
          })}
        </Stack>
      </Stack>
    </Container>
  )
}
